import { trpc } from '@agentic-flow-studio/shared/lib/trpc';
import type { JobPlanVerticalKey } from '@agentic-flow-studio/shared/types/job-plan';
import { Badge } from '@agentic-flow-studio/shared/ui/badge';
import { Button } from '@agentic-flow-studio/shared/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@agentic-flow-studio/shared/ui/card';
import { Input } from '@agentic-flow-studio/shared/ui/input';
import {
  CheckCircle2,
  Clock,
  HelpCircle,
  Loader2,
  MapPin,
  Save,
  Sparkles,
  Tag,
  Wrench,
} from 'lucide-react';
import type React from 'react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

const VERTICAL_OPTIONS: Array<{
  key: JobPlanVerticalKey;
  label: string;
  description: string;
  icon: string;
  examples: string;
}> = [
  {
    key: 'trades',
    label: 'Trades & Maintenance',
    description: 'Contractors, HVAC diagnostics, electrical repairs, plumbing, emergency callouts.',
    icon: '🔧',
    examples: 'HVAC, Split Systems, Switchboards, Burst Pipes, Hot Water Cylinders',
  },
  {
    key: 'logistics',
    label: 'Logistics & Couriers',
    description:
      'Parcel distribution, pallet transport, multi-drop regional manifests, container transport.',
    icon: '🚚',
    examples: 'Pallet Drops, Hazchem Deliveries, Warehouse Transfers, Fleet Runs',
  },
  {
    key: 'it-telecom',
    label: 'IT & Telecommunications',
    description: 'Onsite network cabling, server rack installs, hardware swaps, Wi-Fi deployment.',
    icon: '💻',
    examples: 'Cat6 Runs, Managed Switches, POS Register Swaps, Fiber Patching',
  },
  {
    key: 'healthcare',
    label: 'Healthcare & Mobile Care',
    description: 'Home nursing visits, patient care assessments, mobile phlebotomy, mobility aids.',
    icon: '🩺',
    examples: 'Wound Dressing, In-Home Pathology, Safety Assessments, Rehabilitation',
  },
  {
    key: 'facilities',
    label: 'Facilities & Cleaning',
    description:
      'Commercial deep cleaning, quarterly pest control, exterior grounds & lawn maintenance.',
    icon: '🧹',
    examples: 'Steam Extraction, Termite Inspections, Pressure Washing, Lawn Care',
  },
  {
    key: 'general',
    label: 'General Field Service',
    description:
      'Universal field-service operations, equipment servicing, inspections, and customer repairs.',
    icon: '📋',
    examples: 'Onsite Diagnostics, Preventive Maintenance, Warranty Callouts',
  },
];

export function JobPlanSettingsSection() {
  const utils = trpc.useUtils();
  const { data: settings, isLoading } = trpc.jobPlan.getSettings.useQuery();
  const { data: locations = [] } = trpc.jobPlan.locations.list.useQuery();

  const [verticalKey, setVerticalKey] = useState<JobPlanVerticalKey>('trades');
  const [customRules, setCustomRules] = useState<string>('');
  const [durationMinutes, setDurationMinutes] = useState<number>(60);
  const [windowStart, setWindowStart] = useState<string>('08:00');
  const [windowEnd, setWindowEnd] = useState<string>('17:00');
  const [depotLocationId, setDepotLocationId] = useState<number | null>(null);

  useEffect(() => {
    if (settings) {
      setVerticalKey(settings.verticalKey);
      setCustomRules(settings.customRules || '');
      setDurationMinutes(Math.round((settings.defaultJobDurationSeconds || 3600) / 60));

      const startHours = Math.floor((settings.defaultServiceWindowStart || 28800) / 3600);
      const startMins = Math.floor(((settings.defaultServiceWindowStart || 28800) % 3600) / 60);
      setWindowStart(
        `${String(startHours).padStart(2, '0')}:${String(startMins).padStart(2, '0')}`
      );

      const endHours = Math.floor((settings.defaultServiceWindowEnd || 61200) / 3600);
      const endMins = Math.floor(((settings.defaultServiceWindowEnd || 61200) % 3600) / 60);
      setWindowEnd(`${String(endHours).padStart(2, '0')}:${String(endMins).padStart(2, '0')}`);

      setDepotLocationId(settings.defaultDepotLocationId ?? null);
    }
  }, [settings]);

  const updateMutation = trpc.jobPlan.updateSettings.useMutation({
    onSuccess: () => {
      toast.success('Job Plan settings saved successfully');
      utils.jobPlan.getSettings.invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  const completeOnboardingMutation = trpc.jobPlan.completeOnboarding.useMutation({
    onSuccess: (res) => {
      toast.success(
        `Onboarding completed! ${res.seededSkillCount > 0 ? `Seeded ${res.seededSkillCount} starter skills.` : ''}`
      );
      utils.jobPlan.getSettings.invalidate();
      utils.jobPlan.skills.list.invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();

    const [startH, startM] = windowStart.split(':').map((v) => parseInt(v, 10) || 0);
    const [endH, endM] = windowEnd.split(':').map((v) => parseInt(v, 10) || 0);

    updateMutation.mutate({
      verticalKey,
      customRules: customRules.trim() || null,
      defaultJobDurationSeconds: durationMinutes * 60,
      defaultServiceWindowStart: startH * 3600 + startM * 60,
      defaultServiceWindowEnd: endH * 3600 + endM * 60,
      defaultDepotLocationId: depotLocationId,
    });
  };

  const handleSeedSkills = () => {
    completeOnboardingMutation.mutate({
      verticalKey,
      customRules: customRules.trim() || undefined,
      seedStarterSkills: true,
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12 text-muted-foreground gap-2">
        <Loader2 className="h-5 w-5 animate-spin" />
        <span className="text-sm">Loading Job Plan settings...</span>
      </div>
    );
  }

  return (
    <form onSubmit={handleSave} className="space-y-6 max-w-4xl">
      <div>
        <h1 className="text-xl font-bold tracking-tight">Job Plan Settings</h1>
        <p className="text-xs text-muted-foreground mt-1">
          Configure your domain vertical, AI multimodal extraction guidelines, and scheduling
          presets.
        </p>
      </div>

      {/* Section 1: Business Domain Vertical */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Tag className="h-4 w-4 text-primary" />
            <span>Primary Business Domain Vertical</span>
          </CardTitle>
          <CardDescription className="text-xs">
            Select the vertical that best fits your operations. This educates the Gemini AI engine
            with domain-specific trade vocabulary, arrival patterns, and terminology.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {VERTICAL_OPTIONS.map((opt) => {
              const isSelected = verticalKey === opt.key;
              return (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => setVerticalKey(opt.key)}
                  className={`p-3.5 rounded-xl border text-left flex flex-col justify-between gap-2 transition-all ${
                    isSelected
                      ? 'border-primary bg-primary/[0.04] ring-1 ring-primary shadow-xs'
                      : 'border-border/70 bg-card hover:border-border hover:bg-muted/30'
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xl">{opt.icon}</span>
                    {isSelected && (
                      <Badge className="bg-primary text-primary-foreground text-[10px] gap-1 px-1.5 py-0">
                        <CheckCircle2 className="h-2.5 w-2.5" />
                        Active
                      </Badge>
                    )}
                  </div>

                  <div>
                    <span className="font-semibold text-xs text-foreground block">{opt.label}</span>
                    <p className="text-[11px] text-muted-foreground mt-0.5 leading-relaxed">
                      {opt.description}
                    </p>
                  </div>

                  <div className="pt-2 border-t border-border/40 text-[10px] text-muted-foreground/80 font-mono truncate">
                    <span>{opt.examples}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Section 2: AI Ingestion Custom Rules */}
      <Card className="border-primary/20 bg-primary/[0.01]">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            <span>Workspace AI Ingestion Rules & Prompt Directives</span>
          </CardTitle>
          <CardDescription className="text-xs">
            These guidelines are dynamically injected into the AI system prompt whenever users
            upload screenshots, paste spreadsheets, or ingest jobs.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <textarea
            value={customRules}
            onChange={(e) => setCustomRules(e.target.value)}
            placeholder="e.g.
• Work order numbers always follow the format 'WO-XXXXX'.
• Jobs referencing 'water leak', 'loss of power', or 'urgent' must have priority set to 2 (Urgent).
• Default job duration is 90 minutes for commercial sites, 45 minutes for residential.
• Arrival windows should default to 2-hour slots if exact time is not specified."
            rows={5}
            className="w-full p-3 text-xs rounded-xl border border-border bg-background focus:outline-none focus:ring-1 focus:ring-primary font-mono resize-none leading-relaxed"
          />

          <div className="flex items-center justify-between text-[11px] text-muted-foreground">
            <span className="flex items-center gap-1">
              <HelpCircle className="h-3.5 w-3.5 text-primary" />
              <span>
                Tailor extraction to your company's legacy formats, abbreviations, and rules.
              </span>
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Section 3: Default Scheduling Parameters & Depot */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Clock className="h-4 w-4 text-primary" />
            <span>Default Scheduling & Operating Hours</span>
          </CardTitle>
          <CardDescription className="text-xs">
            Fallback parameters applied when imported work orders do not specify explicit times or
            durations.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-foreground">
              Default Job Duration (Minutes)
            </label>
            <Input
              type="number"
              min={15}
              max={480}
              step={15}
              value={durationMinutes}
              onChange={(e) => setDurationMinutes(parseInt(e.target.value, 10) || 60)}
              className="h-8 text-xs font-mono"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-foreground">
              Daily Service Window Start
            </label>
            <Input
              type="time"
              value={windowStart}
              onChange={(e) => setWindowStart(e.target.value)}
              className="h-8 text-xs font-mono"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-foreground">Daily Service Window End</label>
            <Input
              type="time"
              value={windowEnd}
              onChange={(e) => setWindowEnd(e.target.value)}
              className="h-8 text-xs font-mono"
            />
          </div>

          <div className="space-y-1.5 sm:col-span-3">
            <label className="text-xs font-medium text-foreground flex items-center gap-1">
              <MapPin className="h-3.5 w-3.5 text-primary" />
              <span>Default Base Depot / Warehouse Location</span>
            </label>
            <select
              value={depotLocationId ?? ''}
              onChange={(e) =>
                setDepotLocationId(e.target.value ? parseInt(e.target.value, 10) : null)
              }
              className="w-full h-8 text-xs rounded-lg border border-border bg-background px-2.5 focus:outline-none focus:ring-1 focus:ring-primary"
            >
              <option value="">-- No Default Depot (Use first geocoded location) --</option>
              {locations.map((loc) => (
                <option key={loc.id} value={loc.id}>
                  {loc.label} — {loc.formattedAddress}
                </option>
              ))}
            </select>
            <span className="text-[11px] text-muted-foreground block">
              Used as the starting and ending anchor point for operator route distance calculations.
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Section 4: Vertical Starter Kit Actions */}
      <Card className="border-border/70 bg-muted/20">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Wrench className="h-4 w-4 text-primary" />
            <span>Vertical Starter Skills Catalog</span>
          </CardTitle>
          <CardDescription className="text-xs">
            Quickly seed standard capabilities, trade qualifications, and keyword dictionaries for{' '}
            {VERTICAL_OPTIONS.find((o) => o.key === verticalKey)?.label}.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex items-center justify-between gap-4">
          <p className="text-xs text-muted-foreground">
            Populates your workspace with pre-configured skills equipped with AI matching keywords
            and example jobs.
          </p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleSeedSkills}
            disabled={completeOnboardingMutation.isPending}
            className="text-xs gap-1.5 shrink-0"
          >
            {completeOnboardingMutation.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Sparkles className="h-3.5 w-3.5 text-primary" />
            )}
            <span>Seed Starter Skills</span>
          </Button>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex items-center justify-end gap-3 pt-2">
        <Button
          type="submit"
          disabled={updateMutation.isPending}
          className="gap-2 bg-primary hover:bg-primary/90 h-9 px-5 shadow-xs"
        >
          {updateMutation.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Save className="h-4 w-4" />
          )}
          <span>Save Job Plan Settings</span>
        </Button>
      </div>
    </form>
  );
}
